import { lockIcon } from './icons';
import { t } from './i18n';

const ROOT_CLASS = 'flix-chat-widget__login-required';

// Shopify's New Customer Accounts (passwordless email/phone + code) route
// sign-in through /customer_authentication/login, not the classic
// /account/login — verified live: /account/login silently drops a
// ?return_to= param (a known Shopify issue), while
// /customer_authentication/login carries it through a real OAuth
// authorize request (redirect_uri + state) back to the return_to page.
// There is no separate "register" step in New Customer Accounts — a
// first-time email/phone here creates the account inline — so both
// buttons point at the same endpoint. Overridable via options for
// non-standard setups (e.g. classic accounts, a custom account system).
const DEFAULT_AUTH_URL = '/customer_authentication/login';

export interface LoginRequiredOptions {
  loginUrl?: string;
  registerUrl?: string;
  // Where sign-in should really go, when the host has a better answer than
  // the links above — a Flix Identity sign-in, when the backend honors one.
  // Asked once, the first time this screen is actually shown, so a visitor
  // who never opens the widget costs no request. Resolving to null (or
  // failing) leaves the links exactly as they are, which always work.
  resolveSignInUrl?: () => Promise<string | null>;
  // Called when the visitor follows a link that resolveSignInUrl supplied.
  onSignInStart?: () => void;
  // Set when the visitor IS signed in, but to an account with no customer
  // profile the backend can speak for. A sign-in prompt would be wrong —
  // they have signed in — so this says what is actually the matter.
  //
  // accountUrl is where the visitor completes the link themselves, through
  // the storefront's existing account screen (verify an email, claim
  // previous purchases). Omitted when the host has no such page; the
  // message is then shown on its own.
  linkMissing?: { onRetry: () => void; accountUrl?: string };
}

export interface LoginRequiredScreen {
  element: HTMLElement;
  // Call whenever the screen becomes visible. Idempotent.
  activate(): void;
}

// Sends the customer back to the exact page they were on — computed at
// render time, not baked into a Liquid default, so it works regardless of
// which page the login-required screen happens to render on.
function withReturnTo(url: string): string {
  const returnTo = `${window.location.pathname}${window.location.search}`;
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}return_to=${encodeURIComponent(returnTo)}`;
}

/**
 * Rendered inside the panel body in place of the message list/composer when
 * no customerId is available (the visitor isn't logged in). This is a UX
 * improvement only — see docs/SecurityReview-2026-07-17.md (H1): only
 * logged-in customers may create conversations, unchanged. No guest
 * identity or guest conversation exists anywhere in this widget; this
 * screen exists solely so the launcher doesn't just vanish for a logged-out
 * visitor.
 */
export function createLoginRequiredScreen(options: LoginRequiredOptions = {}): LoginRequiredScreen {
  const container = document.createElement('div');
  container.className = ROOT_CLASS;
  // Direction comes from the widget container (see createWidgetDom) — this
  // screen was previously hardcoded Arabic/RTL, but the storefront is
  // bilingual and the whole widget now follows the page locale.

  const icon = document.createElement('div');
  icon.className = `${ROOT_CLASS}-icon`;
  icon.append(lockIcon());

  const title = document.createElement('h2');
  title.className = `${ROOT_CLASS}-title`;

  const description = document.createElement('p');
  description.className = `${ROOT_CLASS}-description`;

  const actions = document.createElement('div');
  actions.className = `${ROOT_CLASS}-actions`;

  if (options.linkMissing) {
    const { onRetry, accountUrl } = options.linkMissing;
    title.textContent = t('linkMissingTitle');
    description.textContent = t('linkMissingDescription');

    // The way forward, when there is one: the storefront's own account
    // screen, where the link is made by the visitor's own verified email
    // and their own confirmation. Deliberately NOT Shopify's sign-in — that
    // would open a second, unrelated account rather than link this one.
    if (accountUrl) {
      const accountLink = document.createElement('a');
      accountLink.className = `${ROOT_CLASS}-login`;
      accountLink.href = accountUrl;
      accountLink.textContent = t('linkMissingAction');
      actions.append(accountLink);
    }

    // A button, not a link, because it goes nowhere — it asks again. The
    // primary action when it is the only one; secondary beside the link.
    const retryButton = document.createElement('button');
    retryButton.type = 'button';
    retryButton.className = accountUrl ? `${ROOT_CLASS}-register` : `${ROOT_CLASS}-login`;
    retryButton.textContent = t('linkMissingRetry');
    retryButton.addEventListener('click', () => onRetry());

    actions.append(retryButton);
    container.append(icon, title, description, actions);

    return { element: container, activate: () => undefined };
  }

  title.textContent = t('loginTitle');
  description.textContent = t('loginDescription');

  const loginLink = document.createElement('a');
  loginLink.className = `${ROOT_CLASS}-login`;
  loginLink.href = withReturnTo(options.loginUrl ?? DEFAULT_AUTH_URL);
  loginLink.textContent = t('loginAction');

  const registerLink = document.createElement('a');
  registerLink.className = `${ROOT_CLASS}-register`;
  registerLink.href = withReturnTo(options.registerUrl ?? DEFAULT_AUTH_URL);
  registerLink.textContent = t('registerAction');

  actions.append(loginLink, registerLink);
  container.append(icon, title, description, actions);

  let activated = false;

  return {
    element: container,
    activate(): void {
      if (activated || !options.resolveSignInUrl) {
        return;
      }
      activated = true;

      // The links stay real links throughout: until this resolves — and if
      // it never does — they point at the sign-in that has always worked.
      options
        .resolveSignInUrl()
        .then((signInUrl) => {
          if (signInUrl !== null) {
            // The resolved URL carries its own return target; one sign-in
            // serves both buttons, since a first sign-in creates the account.
            loginLink.href = signInUrl;
            registerLink.href = signInUrl;
            if (options.onSignInStart) {
              loginLink.addEventListener('click', options.onSignInStart);
              registerLink.addEventListener('click', options.onSignInStart);
            }
          }
        })
        .catch(() => {
          // Leave the working links in place.
        });
    },
  };
}
